// ============================================================
// Session du panel dans le navigateur : renouvellement pendant le travail, onglets, déconnexion
// ============================================================
// Règles (quand renouveler, comment lire un 401) : sessionPolicy.ts, testé sans navigateur.

import { apiService } from './api.service';
import { authService, TOKEN_STORAGE_KEY } from './auth.service';
import { decodeClaims, readUnauthorized, shouldRefresh } from './sessionPolicy';
import { useAuthStore } from '@/stores/auth.store';
import { useNotificationsStore } from '@/stores/notifications.store';
import { staffUserFromInfo } from '@/devAuth';

/** Connexion Recube : TOUJOURS l'API en direct (jamais le proxy Cloudflare /api/*, qui suit les 302 et casserait le flux). */
export const RECUBE_LOGIN_URL = 'https://api-staff.be.recube.gg/api/auth/recube/login';

/** Posé par l'onglet dont la session a expiré, lu par l'onglet de reconnexion (page de connexion). */
const RECONNECT_KEY = 'recubebe_reconnect_at';
/** Au-delà, une reconnexion commencée et abandonnée ne compte plus. */
const RECONNECT_MAX_AGE_MS = 30 * 60 * 1000;

/** Dernière action de la personne dans cet onglet (clic, touche, molette, retour sur l'onglet). */
let lastActivity = Date.now();
/** Dernier échec du renouvellement (réseau, API en redémarrage) : pas de nouvel essai tout de suite. */
let lastFailure: number | null = null;
/** Un seul renouvellement à la fois dans cet onglet. */
let inFlight: Promise<RefreshOutcome> | null = null;

/**
 * - `renewed` : jeton neuf enregistré ;
 * - `superseded` : le jeton a changé entre-temps (autre onglet, déconnexion), rien n'est écrit ;
 * - `ended` : la session est finie, la fenêtre de reconnexion s'affiche ;
 * - `failed` : échec passager, nouvel essai plus tard.
 */
export type RefreshOutcome = 'renewed' | 'superseded' | 'ended' | 'failed';

export function noteActivity(at: number = Date.now()): void {
  lastActivity = at;
}

/** Renouvelle si la règle le demande (onglet affiché, activité récente, jeton dû) ; null sinon. */
export function maybeRefresh(): Promise<RefreshOutcome> | null {
  const auth = useAuthStore.getState();
  if (!auth.isAuthenticated || auth.sessionEnd) return null;
  const due = shouldRefresh({
    claims: decodeClaims(authService.getToken()),
    nowMs: Date.now(),
    lastActivityMs: lastActivity,
    visible: document.visibilityState === 'visible',
    lastFailureMs: lastFailure,
  });
  return due ? refreshNow() : null;
}

/**
 * Demande un jeton neuf pour la session en cours. Le jeton n'est remplacé que s'il est toujours
 * celui qui a été envoyé : une déconnexion ou un renouvellement fait dans un autre onglet pendant
 * l'appel l'emporte, jamais l'inverse (une session fermée ne revient pas).
 */
export function refreshNow(): Promise<RefreshOutcome> {
  if (inFlight) return inFlight;
  inFlight = (async (): Promise<RefreshOutcome> => {
    const sent = authService.getToken();
    if (!sent) return 'superseded';
    const res = await apiService.refreshSessionToken(sent);
    const stillSame = authService.getToken() === sent && useAuthStore.getState().isAuthenticated;
    if (res.data?.token) {
      if (!stillSame) return 'superseded';
      authService.setToken(res.data.token);
      useAuthStore.getState().setAccessToken(res.data.token);
      lastFailure = null;
      return 'renewed';
    }
    if (res.status === 401 && stillSame) {
      const verdict = readUnauthorized(res.code, true, !authService.isTokenValid(sent));
      if (verdict.kind === 'end') {
        useAuthStore.getState().markSessionEnded(verdict.end);
        return 'ended';
      }
    }
    lastFailure = Date.now();
    return 'failed';
  })().finally(() => {
    inFlight = null;
  });
  return inFlight;
}

/**
 * « Déconnexion » : la session est fermée côté serveur (elle ne se renouvellera plus, même avec
 * une copie du jeton), puis le jeton quitte le navigateur. Les autres onglets suivent (cf
 * installSessionSync).
 */
export function signOut(): void {
  const token = authService.getToken();
  if (token) void apiService.logoutSession(token);
  // Une reconnexion commencée puis abandonnée ne doit pas changer la prochaine connexion normale.
  clearReconnectIntent();
  authService.logout();
  useAuthStore.getState().logout();
}

/**
 * Suit le jeton dans les autres onglets du même navigateur :
 * - jeton retiré (déconnexion ailleurs) : cet onglet se déconnecte aussi ;
 * - jeton neuf et valide alors que la session de cet onglet était finie (reconnexion dans un
 *   autre onglet) : la fenêtre se ferme, la page reprend telle quelle.
 *
 * @returns de quoi arrêter le suivi
 */
export function installSessionSync(): () => void {
  const onStorage = (e: StorageEvent) => {
    // key null : stockage vidé en entier.
    if (e.key !== null && e.key !== TOKEN_STORAGE_KEY) return;
    const auth = useAuthStore.getState();
    if (!auth.isAuthenticated) return;
    const token = authService.getToken();
    if (!token) {
      auth.logout();
      return;
    }
    if (auth.sessionEnd && authService.isTokenValid(token)) {
      auth.clearSessionEnded();
      auth.clearSessionRejected();
      lastFailure = null;
      noteActivity();
      void apiService.getCurrentUser().then((r) => {
        if (r.data) useAuthStore.getState().setUser(staffUserFromInfo(r.data));
      });
      useNotificationsStore.getState().success('Session renouvelée : tu peux reprendre là où tu en étais.');
    }
  };
  window.addEventListener('storage', onStorage);
  return () => window.removeEventListener('storage', onStorage);
}

/** Adresse de reconnexion : la connexion Recube, ou en local la page de connexion (jeton de dev). */
export function reconnectUrl(): string {
  return import.meta.env.DEV ? '/login?reconnexion=1' : RECUBE_LOGIN_URL;
}

/** L'onglet dont la session est finie ouvre la reconnexion : la page de connexion le saura. */
export function markReconnectIntent(): void {
  try {
    localStorage.setItem(RECONNECT_KEY, String(Date.now()));
  } catch {
    // Stockage refusé : la page de connexion se comportera comme d'habitude.
  }
}

/** Une reconnexion est-elle en cours (ouverte depuis un onglet dont la session a expiré) ? */
export function reconnectPending(): boolean {
  try {
    const at = Number(localStorage.getItem(RECONNECT_KEY));
    return Number.isFinite(at) && at > 0 && Date.now() - at < RECONNECT_MAX_AGE_MS;
  } catch {
    return false;
  }
}

export function clearReconnectIntent(): void {
  try {
    localStorage.removeItem(RECONNECT_KEY);
  } catch {
    // Rien à faire.
  }
}
